<template>
    <form id="paycometPaymentForm" data-testid="pago-form" action="javascript:void(0);" method="POST" @submit.prevent="handleSubmit">
        <input id="jetID" type="hidden" data-paycomet="jetID" :value="jet_id" />

        <div class="tw:mb-4">
            <PaycometInputFloating
                :label="$t('formulario_pago.titular')"
                data-paycomet="cardHolderName"
                data-testid="pago-titular"
                required
            />
            <p v-if="errorTitular" class="tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium">{{ errorTitular }}</p>
        </div>

        <div class="tw:mb-4" :class="{ 'tw:border tw:border-red-300 tw:rounded-xl tw:px-3 tw:py-2 tw:bg-red-50': errorPan }">
            <PaycometIframeWrapper
                :label="$t('formulario_pago.n_tarjeta')"
                iframe-div-id="paycomet-pan"
                paycomet-name="pan"
                paycomet-style="width:80%; height:36px; font-size:14px; outline:none; border:0; margin-left: 5px;"
                data-testid="pago-pan-container"
            />
            <p v-if="errorPan" class="tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium">{{ errorPan }}</p>
        </div>

        <div class="tw:mb-6">
            <div class="tw:grid tw:grid-cols-3 tw:gap-3">
                <div class="tw:col-span-1">
                    <PaycometSelect
                        :label="$t('formulario_pago.mes_placeholder')"
                        data-paycomet="dateMonth"
                        data-testid="pago-mes"
                        :placeholder="$t('formulario_pago.mes_placeholder')"
                        :options="monthOptionsList"
                    />
                </div>
                <div class="tw:col-span-1">
                    <PaycometSelect
                        :label="$t('formulario_pago.ano_placeholder')"
                        data-paycomet="dateYear"
                        data-testid="pago-ano"
                        :placeholder="$t('formulario_pago.ano_placeholder')"
                        :options="yearOptionsList"
                    />
                </div>
                <div class="tw:col-span-1">
                    <PaycometIframeWrapper
                        :label="$t('formulario_pago.cvv_label')"
                        iframe-div-id="paycomet-cvc2"
                        paycomet-name="cvc2"
                        paycomet-style="border:0; width:80%; height:36px; font-size:14px; outline:none !important; margin-left:5px"
                        data-testid="pago-cvv-container"
                    />
                    <p v-if="errorCvv" class="tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium">{{ errorCvv }}</p>
                </div>
            </div>
            <p v-if="errorFecha" class="tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium">{{ errorFecha }}</p>
        </div>

        <div id="paymentErrorMsg" class="tw:hidden tw:text-sm tw:text-red-600 tw:bg-red-50 tw:border tw:border-red-200 tw:rounded-xl tw:px-4 tw:py-3 tw:mb-4"></div>
    </form>
</template>

<script setup>
    import { ref, computed, onMounted, onUnmounted } from 'vue';
    import { t } from '@/utils/i18n-helper';
    import { useFormularioPago } from '@/composables/front/common/useFormularioPago.js';
    import { PAN_ERROR_KEYWORDS } from '@/composables/front/common/paycometConstants.js';
    import PaycometInputFloating from './PaycometInputFloating.vue';
    import PaycometSelect from './PaycometSelect.vue';
    import PaycometIframeWrapper from './PaycometIframeWrapper.vue';

    const props = defineProps({
        jet_id: { type: String, required: true },
    });

    const emit = defineEmits(['payToken']);

    const { anosCaducidad, getPayToken } = useFormularioPago(props, emit);

    const errorTitular = ref('');
    const errorPan = ref('');
    const errorFecha = ref('');
    const errorCvv = ref('');

    let paycometErrorObserver = null;

    onMounted(() => {
        // Observar cambios en #paymentErrorMsg para mostrar errores del SDK de Paycomet
        const errorDiv = document.getElementById('paymentErrorMsg');
        if (errorDiv) {
            paycometErrorObserver = new MutationObserver(mutations => {
                for (const mutation of mutations) {
                    if (mutation.type === 'childList' || mutation.type === 'characterData') {
                        const errorText = errorDiv.textContent.trim();
                        if (errorText) {
                            errorDiv.classList.remove('tw:hidden');
                            errorDiv.classList.add('tw:block');
                            if (PAN_ERROR_KEYWORDS.test(errorText)) {
                                errorPan.value = t('formulario_pago.error_tarjeta_invalida');
                            }
                        } else {
                            errorDiv.classList.add('tw:hidden');
                            errorDiv.classList.remove('tw:block');
                            errorPan.value = '';
                        }
                    }
                }
            });
            paycometErrorObserver.observe(errorDiv, { childList: true, characterData: true, subtree: true });
        }
    });

    onUnmounted(() => {
        if (paycometErrorObserver) {
            paycometErrorObserver.disconnect();
            paycometErrorObserver = null;
        }
    });

    const monthOptionsList = Array.from({length: 12}, (_, i) => ({
        value: String(i+1).padStart(2,'0'),
        label: String(i+1).padStart(2,'0')
    }));

    const yearOptionsList = computed(() =>
        anosCaducidad.value.map(a => ({
            value: String(a).slice(-2),
            label: String(a)
        }))
    );

    const clearErrors = () => {
        errorTitular.value = '';
        errorPan.value = '';
        errorFecha.value = '';
        errorCvv.value = '';
    };

    const handleSubmit = () => {
        clearErrors();
        getPayToken();
    };

    const validateHtmlFields = () => {
        let htmlFieldError = false;

        // Validar titular (input HTML)
        const titularInput = document.querySelector('[data-paycomet="cardHolderName"]');
        const titularValue = titularInput?.value?.trim() || '';
        if (!titularValue) {
            errorTitular.value = t('formulario_pago.error_titular_obligatorio');
            htmlFieldError = true;
        }

        // Validar mes y año (selects HTML)
        const monthSelect = document.querySelector('[data-paycomet="dateMonth"]');
        const yearSelect = document.querySelector('[data-paycomet="dateYear"]');
        const monthSelected = monthSelect?.selectedIndex > 0;
        const yearSelected = yearSelect?.selectedIndex > 0;

        if (!monthSelected || !yearSelected) {
            errorFecha.value = t('formulario_pago.error_fecha_obligatoria');
            htmlFieldError = true;
        }

        if (htmlFieldError) {
            if (!titularValue) titularInput?.focus();
            else if (!monthSelected) monthSelect?.focus();
            else if (!yearSelected) yearSelect?.focus();
        }

        return htmlFieldError;
    };

    // El dispatchEvent('submit') es necesario. getPayToken() no inicia el proceso de tokenización — solo lo termina.
    // El SDK de Paycomet necesita que el <form> haga submit para interceptarlo, tokenizar la tarjeta e inyectar el token en el DOM.
    // Llamar directamente a getPayToken() se salta ese paso: el SDK nunca arranca, no hay token, y todo falla silenciosamente.
    const submit = () => {
        clearErrors();

        if (validateHtmlFields()) return;

        // Campos HTML OK → enviamos al SDK para validar iframes (número y CVV)
        // Si están vacíos o inválidos, el SDK escribirá en #paymentErrorMsg
        // y nuestro MutationObserver lo mostrará en rojo.
        const form = document.getElementById('paycometPaymentForm');
        if (form) {
            form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
    };

    defineExpose({ getPayToken, submit });
</script>
